'use client';

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getSubmissions } from '../redux/actions/submissionActions';
import { getOrders } from '../redux/actions/orderActions';
// Import de notre utilitaire de formatage de date
import { formatDateFR } from '../utils/dateFormatter';

const Dashboard = () => {
  const dispatch = useDispatch();
  const { submissions, loading: submissionsLoading } = useSelector(state => state.submissions);
  const { orders, loading: ordersLoading } = useSelector(state => state.orders);
  const { user } = useSelector(state => state.auth);

  // État pour stocker toutes les soumissions (pour les statistiques)
  const [allSubmissions, setAllSubmissions] = useState([]);
  //const [allOrders, setAllOrders] = useState([]);

  const [stats, setStats] = useState({
    totalSubmissions: 0,
    pendingSubmissions: 0,
    approvedSubmissions: 0,
    rejectedSubmissions: 0,
    activeOrders: 0,
    completedOrders: 0
  });

  useEffect(() => {
    // Charger les soumissions récentes
    dispatch(getSubmissions({ limit: 5 }));

    // Charger TOUTES les soumissions pour les statistiques (sans limite)
    dispatch(getSubmissions({ limit: 1000 })).then((response) => {
      if (response && response.items) {
        setAllSubmissions(response.items);
      }
    });
    
    // Charger les commandes récentes
    dispatch(getOrders({ limit: 5 }));
  }, [dispatch]);

  // Calculer les statistiques
  useEffect(() => {
    //if (allSubmissions.length > 0 || allOrders.length > 0) {
    if (allSubmissions.length > 0) {
      setStats({
        totalSubmissions: allSubmissions.length,
        pendingSubmissions: allSubmissions.filter(s => s.status === 'pending').length,
        approvedSubmissions: allSubmissions.filter(s => s.status === 'approved').length,
        rejectedSubmissions: allSubmissions.filter(s => s.status === 'rejected').length,
        //activeOrders: allOrders.filter(o => ['completed', 'pending', 'accepted', 'in_progress'].includes(o.status)).length,
        //completedOrders: allOrders.filter(o => o.status === 'completed').length
      });
    }
  }, [allSubmissions]);

  // Prendre seulement les 5 soumissions les plus récentes pour l'affichage
  const recentSubmissions = submissions?.items?.slice(0, 5) || [];

  return (
    <div className="dashboard-container">
      <div className="page-header">
        <h1>Tableau de bord</h1>
        <Link to="/submissions/new" className="btn btn-primary">
          <i className="material-icons">add</i>
          Nouvelle soumission
        </Link>
      </div>

      <div className="welcome-card">
        <div className="welcome-content">
          <h2>Bienvenue, {user?.firstName} 👋</h2>
          <p>Voici un aperçu de vos activités sur Alphafusion File Manager.</p>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">
            <i className="material-icons">inbox</i>
          </div>
          <div className="stat-content">
            <h3>Total soumissions</h3>
            <div className="stat-value">{stats.totalSubmissions}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon pending">
            <i className="material-icons">hourglass_empty</i>
          </div>
          <div className="stat-content">
            <h3>En attente</h3>
            <div className="stat-value">{stats.pendingSubmissions}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon approved">
            <i className="material-icons">check_circle</i>
          </div>
          <div className="stat-content">
            <h3>Approuvées</h3>
            <div className="stat-value">{stats.approvedSubmissions}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon rejected">
            <i className="material-icons">cancel</i>
          </div>
          <div className="stat-content">
            <h3>Rejetées</h3>
            <div className="stat-value">{stats.rejectedSubmissions}</div>
          </div>
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-card">
          <div className="card-header">
            <h2>Soumissions récentes</h2>
            <Link to="/submissions" className="view-all">
              Voir tout <i className="material-icons">chevron_right</i>
            </Link>
          </div>

          <div className="card-content">
            {submissionsLoading ? (
              <div className="loading-spinner">Chargement...</div>
            ) : recentSubmissions.length > 0 ? (
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Nom</th>
                      <th>Date</th>
                      <th>Statut</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentSubmissions.map(submission => (
                      <tr key={submission.id}>
                        <td>#{submission.id}</td>
                        <td>{submission.name}</td>
                        <td>{formatDateFR(submission.submissionDate)}</td>
                        <td>
                          <span className={`status-badge ${submission.status}`}>
                            {submission.status === 'pending' && 'En attente'}
                            {submission.status === 'approved' && 'Approuvé'}
                            {submission.status === 'rejected' && 'Rejeté'}
                            {submission.status === 'in_progress' && 'En cours'}
                          </span>
                        </td>
                        <td>
                          <Link to={`/submissions/${submission.id}`} className="btn-icon">
                            <i className="material-icons">visibility</i>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <i className="material-icons">inbox</i>
                <p>Aucune soumission pour le moment</p>
                <Link to="/submissions/new" className="btn btn-primary">
                  Créer une soumission
                </Link>
              </div>
            )}
          </div>
        </div>

        <div className="dashboard-card">
          <div className="card-header">
            <h2>Commandes récentes</h2>
            <Link to="/orders" className="view-all">
              Voir tout <i className="material-icons">chevron_right</i>
            </Link>
          </div>

          <div className="card-content">
            {ordersLoading ? (
              <div className="loading-spinner">Chargement...</div>
            ) : orders?.items?.length > 0 ? (
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Soumission</th>
                      <th>Date</th>
                      <th>Statut</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.items.map(order => (
                      <tr key={order.id}>
                        <td>#{order.id}</td>
                        <td>
                          <Link to={`/submissions/${order.submission.id}`}>
                            #{order.submission.id} - {order.submission.name}
                          </Link>
                        </td>
                        <td>{formatDateFR(order.createdAt)}</td>
                        <td>
                          <span className={`status-badge ${order.status}`}>
                            {order.status === 'pending' && 'En attente'}
                            {order.status === 'accepted' && 'Accepté'}
                            {order.status === 'in_progress' && 'En cours'}
                            {order.status === 'completed' && 'Terminé'}
                            {order.status === 'sent' && 'Expédié'}
                            {order.status === 'cancelled' && 'Annulé'}
                          </span>
                        </td>
                        <td>
                          <Link to={`/orders/${order.id}`} className="btn-icon">
                            <i className="material-icons">visibility</i>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <i className="material-icons">shopping_cart</i>
                <p>Aucune commande pour le moment</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;